<template>
  <div class="pph" :class="{ 'pph--page': !closable }" data-test="pp-header">
    <button
      v-if="closable"
      type="button"
      class="pph__close"
      aria-label="Close patient panel"
      data-test="pp-close"
      @click="$emit('close')"
    >&times;</button>

    <div class="pph__id">
      <slot name="avatar"><div class="pph__avatar" aria-hidden="true">{{ initials }}</div></slot>
      <div class="pph__who">
        <div class="pph__name-row">
          <h2 class="pph__name" data-test="pp-name">{{ fullName }}</h2>
          <span v-if="metaLine" class="pph__meta" data-test="pp-meta">{{ metaLine }}</span>
          <span v-if="premed" class="pp-pill pp-pill--rose" data-test="pp-premed">
            <i class="fa fa-medkit" aria-hidden="true"></i> PREMED
          </span>
          <span
            v-if="appointmentMeta && appointmentMeta.statusText && !appointmentMeta.broken"
            class="pp-pill"
            :class="`pp-pill--${statusTone}`"
            data-test="pp-status"
          >{{ appointmentMeta.statusText }}</span>
          <span v-if="appointmentMeta && appointmentMeta.broken" class="pp-pill pp-pill--rose">Broken</span>
          <slot name="extra"></slot>
        </div>

        <div class="pph__line">
          <span v-if="primaryPhone" class="pph__i"><i class="fa fa-phone" aria-hidden="true"></i>{{ primaryPhone }}</span>
          <span v-if="patient && patient.Email" class="pph__i pph__i--email">
            <i class="fa fa-envelope-o" aria-hidden="true"></i>{{ patient.Email }}
          </span>
          <span v-if="visitLine" class="pph__i" data-test="pp-visit">
            <i class="fa fa-calendar" aria-hidden="true"></i>{{ visitLine }}
          </span>
          <span v-if="chairLine" class="pph__i"><i class="fa fa-home" aria-hidden="true"></i>{{ chairLine }}</span>
        </div>

        <div class="pph__facts" data-test="pp-facts">
          <div v-if="carrier" class="pph__fact" data-test="pp-fact-insurance">
            <span class="pph__fact-ic"><i class="fa fa-shield" aria-hidden="true"></i></span>
            <span class="pph__fact-text">
              <span class="pph__k">Insurance</span>
              <span class="pph__v">{{ carrier }}</span>
            </span>
          </div>
          <div
            v-if="insurance"
            class="pph__fact"
            :class="{ 'pph__fact--action': hasVerifiedAction() }"
            data-test="pp-fact-verified"
          >
            <span class="pph__fact-ic"><i class="fa fa-clock-o" aria-hidden="true"></i></span>
            <span class="pph__fact-text">
              <span class="pph__k">Eligibility last verified</span>
              <span class="pph__v">
                <span v-if="verifiedOn" :class="insurance.isStale ? 'pph__bad' : 'pph__ok'">{{ verifiedOn }}</span>
                <span v-else class="pph__bad">Never verified</span>
              </span>
            </span>
            <!-- The host's Verify / Re-check button (the Patient Profile page). Unfilled, the chip is as it was. -->
            <slot name="verified-action"></slot>
          </div>
          <div v-if="patient" class="pph__fact" data-test="pp-fact-recall">
            <span class="pph__fact-ic"><i class="fa fa-refresh" aria-hidden="true"></i></span>
            <span class="pph__fact-text">
              <span class="pph__k">Recall</span>
              <span class="pph__v">{{ recallText }}</span>
            </span>
          </div>
          <div v-if="balance > 0" class="pph__fact" data-test="pp-fact-balance">
            <span class="pph__fact-ic"><i class="fa fa-dollar" aria-hidden="true"></i></span>
            <span class="pph__fact-text">
              <span class="pph__k">Balance</span>
              <span class="pph__v">{{ balanceText }}</span>
            </span>
          </div>
        </div>

        <router-link
          v-if="profileLink"
          :to="profileLink"
          class="pph__full"
          data-test="pp-open-profile"
        >Open full profile <i class="fa fa-arrow-right" aria-hidden="true"></i></router-link>
      </div>
    </div>

    <!-- The action bar (Phase 8) — the design puts it between who the patient is and what needs attention. -->
    <slot name="actions"></slot>

    <!--
      Needs attention — the SAME alerts the schedule block shows, from the same payload and the
      same rule (switched on by the office AND worked out by the server), spelled out as sentences.
      Built by the panel shell so a block and this grid can never disagree.
    -->
    <div v-if="tiles.length" class="pph__attention" data-test="pp-attention">
      <div class="pph__attention-head">
        <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
        <span class="pph__attention-title">Needs attention</span>
        <span class="pph__attention-n">{{ tiles.length }} item{{ tiles.length === 1 ? '' : 's' }}</span>
      </div>
      <div class="pph__grid">
        <div
          v-for="t in tiles"
          :key="t.key"
          class="pph__tile"
          :data-test="`pp-tile-${t.key}`"
        >
          <i class="fa pph__tile-ic" :class="t.icon" aria-hidden="true"></i>
          <span class="pph__tile-text">
            <span class="pph__tile-t">{{ t.label }}</span>
            <span class="pph__tile-s">{{ t.sentence }}</span>
          </span>
        </div>
      </div>
      <p v-if="attentionNote" class="pph__attention-note" data-test="pp-attention-note">{{ attentionNote }}</p>
    </div>
  </div>
</template>

<script>
/**
 * The top of the Patient Panel — the design's `TmHeader` (layout 1A).
 *
 * Read-only in Phase 7: the design's action bar (Follow-up, Send form/text/email, Tx plans, Create
 * task, Review, Photo to OD, Form QR, Payment) is Phase 8, which reuses Patient Profile's own
 * modals. Until then the one action here is "Open full profile".
 *
 * Deliberately absent, because nothing in Teamio loads them today (Phase 7 decision, 2026-09-25):
 * the referral-source tile and "self-pay" fallback. Insurance shows only when the office has
 * Insurance Verification, which is the only source of the carrier.
 *
 * ⛔ No prop named `patNum`, `siteID` or `loading` — mixin.js computeds silently replace them.
 */
import {
  ageOn, formatOdDate, patientFullName, patientInitials, phoneLines, money, procedureItems,
} from '@/utils/patientPanel/format';
import { appointmentSpan, formatTimeRange } from '@/utils/scheduleView/layout';

const STATUS_TONE = { confirmed: 'green', arrived: 'blue', unconfirmed: 'amber', completed: 'slate' };

export default {
  name: 'PatientPanelHeader',

  props: {
    /** The patient row from /schedule-view/patient, or null while it loads. */
    patient: { type: Object, default: null },
    /** The live appointment the panel was opened from, or null. */
    appointment: { type: Object, default: null },
    /** { opName, providerName, bucket, statusText, broken } resolved by the host. */
    appointmentMeta: { type: Object, default: null },
    /** Insurance Verification's patient-status payload, or null (off, or not loaded). */
    insurance: { type: Object, default: null },
    /** [{ key, icon, label, sentence }] */
    tiles: { type: Array, default: () => [] },
    today: { type: String, default: '' },
    dateFormat: { type: String, default: 'MM/DD/YYYY' },
    /** Route to Patient Profile, or '' to hide the link. */
    profileLink: { type: String, default: '' },
    /** The × button — the panel has one, the Patient Profile page does not. */
    closable: { type: Boolean, default: true },
    /** A line under the Needs attention tiles, or ''. */
    attentionNote: { type: String, default: '' },
  },

  computed: {
    preferred() {
      const p = this.appointment && this.appointment.patient;
      return (p && p.Preferred) || '';
    },

    /** The row, or the appointment's own name while the full row loads — never a blank header. */
    nameSource() {
      return this.patient || (this.appointment && this.appointment.patient) || null;
    },

    fullName() {
      return patientFullName(this.nameSource, this.preferred);
    },

    initials() {
      return patientInitials(this.nameSource, this.preferred);
    },

    metaLine() {
      if (!this.patient) return '';
      const parts = [];
      const age = ageOn(this.patient.Birthdate, this.today);
      if (age !== null) parts.push(String(age));
      const dob = formatOdDate(this.patient.Birthdate, this.dateFormat);
      if (dob) parts.push(`DOB ${dob}`);
      return parts.join(' · ');
    },

    premed() {
      return !!(this.patient && Number(this.patient.Premed) === 1);
    },

    statusTone() {
      const b = this.appointmentMeta && this.appointmentMeta.bucket;
      return STATUS_TONE[b] || 'slate';
    },

    primaryPhone() {
      const first = phoneLines(this.patient)[0];
      return first ? first.value : '';
    },

    visitLine() {
      if (!this.appointment) return '';
      const span = appointmentSpan(this.appointment);
      const time = span ? formatTimeRange(span.start, span.end) : '';
      const firstProc = procedureItems(this.appointment.ProcDescript)[0] || '';
      return ['This visit', [firstProc, time].filter(Boolean).join(' · ')].filter(Boolean).join(': ');
    },

    chairLine() {
      const m = this.appointmentMeta || {};
      return [m.opName, m.providerName].filter(Boolean).join(' · ');
    },

    /** The primary plan's carrier, from Insurance Verification's plan list. */
    carrier() {
      const plans = (this.insurance && this.insurance.plans) || [];
      const primary = plans[0] && plans[0].insurance;
      return (primary && primary.carrierName) || '';
    },

    verifiedOn() {
      if (!this.insurance || this.insurance.neverVerified) return '';
      return formatOdDate(this.insurance.lastVerified, this.dateFormat);
    },

    recallText() {
      const d = this.patient && formatOdDate(this.patient.RecallScheduled, this.dateFormat);
      return d ? `Scheduled ${d}` : 'Not scheduled';
    },

    /** Open Dental's estimated balance — the figure Patient Profile's header shows as "Balance". */
    balance() {
      const n = Number(this.patient && this.patient.EstBalance);
      return Number.isFinite(n) ? n : 0;
    },

    balanceText() {
      return money(this.balance);
    },
  },

  methods: {
    /**
     * Does the host's #verified-action slot draw anything right now? A scoped slot that renders only
     * comments (the page's v-if is false) answers `undefined`, so the chip can widen for a button
     * only while there is one. A method, not a computed: $scopedSlots is not reactive, and the
     * slot's own reads must be tracked by THIS render.
     */
    hasVerifiedAction() {
      const slot = this.$scopedSlots['verified-action'];
      return !!(slot && slot());
    },
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

.pph {
  position: relative;
  // The bottom padding belongs to the header (white), not a margin on the tabs, which showed the
  // panel's grey background as a stripe between Needs attention and the tab strip.
  padding: 18px 26px 18px;
  background: $pp-surface;
}

.pph__close {
  position: absolute;
  top: 14px;
  right: 18px;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 10px;
  background: $pp-bg-3;
  color: $pp-ink-2;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;

  &:hover { background: $pp-line; }
}

.pph__id {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding-right: 44px;
}

// The page has no close button, so no room is kept for one.
.pph--page .pph__id { padding-right: 0; }

.pph__avatar {
  flex: 0 0 auto;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: $pp-brand-soft;
  color: $pp-brand;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
  font-weight: 700;
}

.pph__who {
  flex: 1 1 auto;
  min-width: 0;
}

.pph__name-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 11px;
}

.pph__name {
  margin: 0;
  font-size: 27px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: $pp-ink;
  overflow-wrap: anywhere;
}

.pph__meta {
  font-size: 14px;
  font-weight: 500;
  color: $pp-ink-3;
}

.pph__line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 18px;
  margin-top: 9px;
  font-size: 13px;
  color: $pp-ink-2;
}

.pph__i {
  display: inline-flex;
  // Top-aligned so an item that wraps on a phone ("This visit: … am") keeps its icon on the
  // first line instead of floating between the two.
  align-items: flex-start;
  gap: 7px;
  min-width: 0;

  .fa { margin-top: 3px; color: $pp-ink-4; }
}

.pph__i--email {
  overflow-wrap: anywhere;
}

.pph__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 13px;
}

.pph__fact {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  padding: 7px 12px;
  border: 1px solid $pp-line;
  border-radius: 12px;
  background: $pp-bg-2;
}

.pph__fact-ic {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $pp-brand-soft;
  color: $pp-brand;
  font-size: 12px;
}

.pph__fact-text {
  min-width: 0;
}

.pph__k {
  display: block;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: $pp-ink-4;
}

.pph__v {
  display: block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12.5px;
  font-weight: 700;
  color: $pp-ink;
}

.pph__ok { color: $pp-brand; }
.pph__bad { color: $pp-rose-ink; }

.pph__full {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  font-size: 13px;
  font-weight: 700;
  color: $pp-brand;

  &:hover { color: $pp-brand-deep; text-decoration: underline; }
}

.pph__attention {
  margin: 16px 0 0;
  border: 1px solid $pp-line;
  border-radius: $pp-radius-lg;
  background: $pp-surface;
  overflow: hidden;
}

.pph__attention-head {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 14px 18px 0;

  .fa { color: $pp-attn-ink; font-size: 16px; }
}

.pph__attention-title {
  flex: 1 1 auto;
  font-size: 15px;
  font-weight: 800;
  color: $pp-ink;
}

.pph__attention-n {
  font-size: 11.5px;
  color: $pp-ink-3;
}

.pph__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 12px;
  padding: 14px 18px 18px;
}

.pph__tile {
  display: flex;
  gap: 11px;
  padding: 13px 15px;
  border: 1px solid $pp-attn-line;
  border-radius: 13px;
  background: $pp-attn-bg;
}

.pph__tile-ic {
  flex: 0 0 auto;
  width: 16px;
  margin-top: 2px;
  text-align: center;
  color: $pp-attn-ink;
}

.pph__tile-text { min-width: 0; }

.pph__tile-t {
  display: block;
  font-size: 13.5px;
  font-weight: 700;
  color: $pp-ink;
}

.pph__attention-note {
  margin: 0;
  padding: 0 18px 14px;
  font-size: 11.5px;
  color: $pp-ink-3;
}

.pph__tile-s {
  display: block;
  margin-top: 3px;
  font-size: 12.5px;
  line-height: 1.4;
  color: $pp-ink-2;
}

// The header's own pills. The tabs get the same classes from the panel shell (::v-deep).
.pp-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

.pp-pill--green { background: $pp-green-bg; color: $pp-green-ink; }
.pp-pill--amber { background: $pp-amber-bg; color: $pp-amber-ink; }
.pp-pill--rose { background: $pp-rose-bg; color: $pp-rose-ink; }
.pp-pill--blue { background: $pp-blue-bg; color: $pp-blue-ink; }
.pp-pill--slate { background: $pp-slate-bg; color: $pp-slate-ink; }

@media (max-width: $pp-stack-max) {
  .pph { padding: 16px 20px 16px; }
  .pph__name { font-size: 24px; }
}

@media (max-width: $pp-phone-max) {
  .pph { padding: 14px 16px 14px; }
  // The phone has the shell's back bar instead.
  .pph__close { display: none; }
  .pph__id { gap: 12px; padding-right: 0; }
  .pph__avatar { width: 48px; height: 48px; font-size: 16px; }
  .pph__name { font-size: 21px; }
  .pph__meta { font-size: 12.5px; }
  .pph__line { gap: 6px 14px; margin-top: 7px; font-size: 12.5px; }
  .pph__facts {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 12px;
  }
  .pph__fact { padding: 7px 10px; }
  // A chip that holds the host's button takes the whole row (a half-width chip has no room for the
  // text AND the button); its text grows so the button sits at the right edge. With two buttons
  // (Verify / Re-check + View benefits) the pair drops to its own line when it does not fit beside the text.
  .pph__fact--action { grid-column: 1 / -1; flex-wrap: wrap; }
  .pph__fact--action .pph__fact-text { flex: 1 1 auto; }
  .pph__v { max-width: none; font-size: 12px; }
  .pph__attention { margin: 14px -16px 0; border-radius: 0; border-left: 0; border-right: 0; }
  .pph__attention-head { padding: 13px 16px 0; }
  .pph__grid { grid-template-columns: 1fr; gap: 10px; padding: 12px 16px 16px; }
}
</style>
